import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import React, { useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Stack,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Flex,
  Input,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { STATUS_LIST } from "services/collectionsService";
import { FilterContainer } from "components/Tables/FilterContainer";
import SearchSelect from "components/SearchSelect/SearchSelect";
import DateTime from "react-datetime";
import { LOCALE_PT_BR } from "constants/date-time";
import {
  aitMask,
  validateAitNumber,
  plateMask,
  validateVehiclePlate,
} from "utils/forms-utils";
import { parseDateBrToISO } from "utils/utils";

export const CollectionsFilter = (props) => {
  const { t } = useTranslation("common");
  const { onFilter, filterSnapshot } = props;
  const formSpacing = "30px";
  const cardSpacing = "20px";
  const { isOpen, onToggle } = useDisclosure();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [plate, setPlate] = useState("");
  const [status, setStatus] = useState("");
  const [aitNumber, setAitNumber] = useState("");
  const placeholderColor = useColorModeValue("gray.300", "gray.400");

  const isStartDateInvalid = startDate ? startDate > new Date() : false;
  const isEndDateInvalid = startDate && endDate ? endDate <= startDate : false;
  const [aitNumberError, setAitNumberError] = useState(null);
  const [plateError, setPlateError] = useState(null);

  const handleAitNumberChange = (e) => {
    const maskedAit = aitMask(e.target.value);
    setAitNumber(maskedAit);
    if (aitNumberError) setAitNumberError(null);
  };

  const handlePlateChange = (e) => {
    const maskedPlate = plateMask(e.target.value);
    setPlate(maskedPlate);
    if (plateError) setPlateError(null);
  };

  const onFilterHandler = () => {
    onFilter({
      data_inicial: startDate
        ? new Date(startDate).toLocaleDateString(LOCALE_PT_BR)
        : "",
      data_final: endDate
        ? new Date(endDate).toLocaleDateString(LOCALE_PT_BR)
        : "",
      numero_ait: aitNumber,
      situacao: status,
      placa: plate,
    });
  };

  const onClearHandler = () => {
    setStartDate("");
    setEndDate("");
    setPlate("");
    setStatus("");
    setAitNumber("");
    setPlateError(null);
    setAitNumberError(null);

    onFilter({
      data_inicial: null,
      data_final: null,
      numero_ait: null,
      situacao: null,
      placa: null,
    });
  };

  const handleToggle = () => {
    if (isOpen) {
      setPlateError(null);
      setAitNumberError(null);
    }
    onToggle();
  };

  useLayoutEffect(() => {
    if (filterSnapshot == null) {
      setStartDate("");
      setEndDate("");
      setPlate("");
      setStatus("");
      setAitNumber("");
      return;
    }
    setPlate(filterSnapshot.placa ? String(filterSnapshot.placa) : "");
    setStatus(filterSnapshot.situacao ? String(filterSnapshot.situacao) : "");
    setAitNumber(filterSnapshot.numero_ait ? String(filterSnapshot.numero_ait) : "");
    if (filterSnapshot.data_inicial) {
      const d = parseDateBrToISO(filterSnapshot.data_inicial);
      setStartDate(d && !isNaN(d.getTime()) ? d : "");
    } else {
      setStartDate("");
    }
    if (filterSnapshot.data_final) {
      const d = parseDateBrToISO(filterSnapshot.data_final);
      setEndDate(d && !isNaN(d.getTime()) ? d : "");
    } else {
      setEndDate("");
    }
  }, [filterSnapshot]);

  return (
    <FilterContainer
      w="125px"
      fontSize="sm"
      fontWeight="bold"
      variant="light"
      isOpen={isOpen}
      onToggle={handleToggle}
      onFilter={onFilterHandler}
      onClear={onClearHandler}
    >
      <Flex w="100%" gap="4" justify="space-between">
        <Stack w="100%" direction="column" spacing={cardSpacing}>
          <FormControl isInvalid={!!plateError}>
            <FormLabel>
              {t("digital-collections.filters.fields.plate")}
            </FormLabel>
            <Input
              fontSize="sm"
              placeholder={t("digital-collections.filters.placeholders.plate")}
              _placeholder={{ color: placeholderColor, fontSize: "sm" }}
              value={plate}
              onBlur={() => {
                const err = validateVehiclePlate(
                  plate,
                  t("digital-collections.filters.fields.plate"),
                );
                setPlateError(err);
              }}
              onChange={handlePlateChange}
            />
            <FormErrorMessage>{plateError && t(plateError)}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!aitNumberError}>
            <FormLabel>
              {t("digital-collections.filters.fields.ait-number")}
            </FormLabel>
            <Input
              fontSize="sm"
              placeholder={t(
                "digital-collections.filters.placeholders.ait-number",
              )}
              _placeholder={{ color: placeholderColor, fontSize: "sm" }}
              value={aitNumber}
              onBlur={() => {
                const err = validateAitNumber(
                  aitNumber,
                  t("digital-collections.filters.fields.ait-number"),
                );
                setAitNumberError(err);
              }}
              onChange={handleAitNumberChange}
            />
            <FormErrorMessage>
              {aitNumberError && t(aitNumberError)}
            </FormErrorMessage>
          </FormControl>

          <FormControl>
            <FormLabel>
              {t("digital-collections.filters.fields.status")}
            </FormLabel>
            <SearchSelect
              items={STATUS_LIST}
              name="status"
              valueField="id"
              displayField="status"
              value={status}
              onChange={(e) => setStatus(e.currentTarget.value)}
              placeholder={t("digital-collections.filters.placeholders.status")}
            />
          </FormControl>

          <Stack w="100%" direction="row" spacing={formSpacing}>
            <FormControl w="230px" isInvalid={isStartDateInvalid}>
              <FormLabel>{t("system.labels.start-date")}</FormLabel>
              <DateTime
                locale={LOCALE_PT_BR}
                closeOnSelect
                timeFormat={false}
                inputProps={
                  startDate
                    ? undefined
                    : { value: "", placeholder: t("system.placeholders.date") }
                }
                renderInput={(props) => (
                  <Input
                    {...props}
                    fontSize="sm"
                    _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                  />
                )}
                onChange={(date) => setStartDate(date)}
              />
              <FormErrorMessage>
                {t("system.messages.errors.invalid-start-date")}
              </FormErrorMessage>
            </FormControl>

            <FormControl w="230px" isInvalid={isEndDateInvalid}>
              <FormLabel>{t("system.labels.end-date")}</FormLabel>
              <DateTime
                locale={LOCALE_PT_BR}
                closeOnSelect
                timeFormat={false}
                inputProps={
                  endDate
                    ? undefined
                    : { value: "", placeholder: t("system.placeholders.date") }
                }
                renderInput={(props) => (
                  <Input
                    {...props}
                    fontSize="sm"
                    _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                  />
                )}
                onChange={(date) => setEndDate(date)}
              />
              <FormErrorMessage>
                {t("system.messages.errors.invalid-end-date")}
              </FormErrorMessage>
            </FormControl>
          </Stack>
        </Stack>
      </Flex>
    </FilterContainer>
  );
};
